/**
 * 목차 스크롤 추적의 기하 계산 (순수 함수).
 *
 * ⚠️ 관측 띠 비율과 꼬리 여백은 **반드시 같은 상수에서 파생**되어야 한다.
 * 따로 두면 한쪽만 바뀔 때 "마지막 제목이 선택되지 않는" 증상으로 조용히 깨진다.
 */

/**
 * 제목이 활성으로 잡히는 화면 상단 띠의 비율.
 *
 * 이 띠를 좁게 두는 이유는 화면 **아래쪽** 제목이 먼저 잡히는 것을 막기 위해서다.
 * 대신 그 대가로 문서 **끝쪽** 제목들이 띠에 도달하지 못하는 문제가 생기며,
 * 그건 `computeTailSpacer`가 만드는 여백으로 해소한다.
 */
export const TOC_BAND_RATIO = 0.3;

/** IntersectionObserver rootMargin — 위 비율에서 파생(하드코딩 금지). */
export const TOC_ROOT_MARGIN = `0px 0px -${Math.round((1 - TOC_BAND_RATIO) * 100)}% 0px`;

export interface ScrollExtent {
  scrollTop: number;
  /** 보이는 높이 (clientHeight 또는 innerHeight) */
  viewport: number;
  /** 전체 콘텐츠 높이 (scrollHeight) */
  total: number;
}

/**
 * 스크롤이 바닥에 닿았는지 — 목차 마지막 항목을 확정 활성화할 조건(안전망).
 *
 * ⚠️ **전부 한 화면에 들어오는 문서는 false**여야 한다. 그러지 않으면 맨 위를 보고 있는데
 * 마지막 제목이 활성으로 뜬다(`scrollTop=0`인데 `scrollTop+viewport >= total`이 성립).
 */
export function isScrolledToBottom({ scrollTop, viewport, total }: ScrollExtent): boolean {
  if (total - viewport <= 8) return false;
  return scrollTop + viewport >= total - 4;
}

export interface TailSpacerInput {
  /** 보이는 높이 */
  viewport: number;
  /** 마지막 제목 위쪽 경계부터 본문 끝까지의 높이 */
  contentBelowLastHeading: number;
}

/**
 * 본문 끝에 덧붙일 여백 높이.
 *
 * **왜 필요한가**: 제목은 화면 상단 띠(`TOC_BAND_RATIO`)에 들어와야 활성이 된다. 그런데 문서
 * 끝에서는 스크롤이 멈춰 버려서, **마지막 화면 한 장 안에 있는 제목들은 그 띠까지 올라올 수
 * 없다**. 그래서 끝까지 내려도 활성 표시가 중간 제목에 멈춘다. 마지막 제목이 띠에 닿을 만큼만
 * 여백을 주면 그 앞 제목들도 자동으로 각자의 활성 구간을 갖는다.
 *
 * 필요한 만큼만 준다 — 콘텐츠가 이미 충분하면 0이라 빈 공간이 생기지 않는다.
 */
export function computeTailSpacer({ viewport, contentBelowLastHeading }: TailSpacerInput): number {
  const needed = Math.round(viewport * (1 - TOC_BAND_RATIO));
  return Math.max(0, needed - Math.max(0, contentBelowLastHeading));
}
